import { motion } from "framer-motion";
import { Users, GitBranch, ArrowRight, TrendingUp, Zap, Award, ChevronRight, Network } from "lucide-react";
import globalNetwork from "../assets/global-network.jpg";
import Section from "../components/Section";
import SectionHeading from "../components/SectionHeading";
import PageHero from "../components/PageHero";
import EnergyParticles from "../components/EnergyParticles";
import StatsGrid from "../components/StatsGrid";
import { Link } from "react-router-dom";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const AffiliateNetwork = () => (
  <div>
    <PageHero
      eyebrow="Affiliate & Network"
      title={<>Build Your <span className="energy-gradient-text">Renewable Network</span></>}
      description="Earn through a powerful binary team structure and a 5-level referral system. Grow your network and earn matching bonuses as your team participates in clean energy projects."
      backgroundImage={globalNetwork}
    />

    <Section>
      <StatsGrid stats={[
        { value: "5 Levels", label: "Referral Depth" },
        { value: "23%", label: "Total Referral Pool" },
        { value: "10%", label: "Binary Matching" },
        { value: "Unlimited", label: "Team Size" },
      ]} />
    </Section>

    {/* Binary Structure */}
    <Section className="bg-secondary/20 relative overflow-hidden">
      <EnergyParticles />
      <div className="relative z-10">
        <SectionHeading eyebrow="Binary System" title="Dual-Team Binary Structure" description="Build a left and right team. Earn matching bonuses based on the weaker leg's performance — incentivizing balanced team growth." />
        <motion.div {...fadeUp} className="max-w-3xl mx-auto p-8 rounded-2xl glass-card">
          <div className="flex flex-col items-center">
            <div className="w-20 h-20 rounded-full energy-gradient flex items-center justify-center mb-3 animate-glow-pulse">
              <span className="font-display text-base font-bold text-primary-foreground">YOU</span>
            </div>
            <div className="text-xs text-muted-foreground mb-6">Your Binary Position</div>

            {/* Vertical connector */}
            <div className="w-px h-8 bg-primary/30" />
            <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            {/* Horizontal connector */}
            <div className="w-64 h-px bg-primary/30" />

            <div className="grid grid-cols-2 gap-20 w-full max-w-lg mt-4">
              {[
                { side: "Left Team", desc: "Power Leg — grow for volume", nodes: ["A1", "A2", "A3", "A4"] },
                { side: "Right Team", desc: "Profit Leg — matched for bonus", nodes: ["B1", "B2", "B3", "B4"] },
              ].map((team, i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-14 h-14 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center mb-2">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div className="font-display text-sm font-semibold">{team.side}</div>
                  <div className="text-[10px] text-muted-foreground mb-4">{team.desc}</div>
                  {/* Sub tree */}
                  <div className="grid grid-cols-2 gap-3">
                    {team.nodes.map((n, j) => (
                      <motion.div key={j} {...fadeUp} transition={{ delay: 0.3 + j * 0.1 }} className="w-10 h-10 rounded-full bg-secondary/60 border border-border flex items-center justify-center hover:border-primary/30 transition-colors cursor-pointer">
                        <span className="text-[10px] text-muted-foreground font-medium">{n}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-4 rounded-xl bg-primary/5 border border-primary/20 max-w-md">
              <div className="flex items-center gap-2 mb-2">
                <GitBranch className="w-4 h-4 text-primary" />
                <span className="text-xs font-semibold">How Matching Works</span>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Binary bonus is calculated on the weaker leg's new volume each cycle (up to 10% matching). This encourages balanced growth across both teams rather than lopsided recruitment.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>

    {/* 5-Level Referral */}
    <Section>
      <SectionHeading eyebrow="Referral Program" title="5-Level Referral Commissions" description="Earn referral bonuses on every package activation across 5 generations of your network." />
      <div className="max-w-2xl mx-auto space-y-3">
        {[
          { level: "Level 1 — Direct Referral", pct: "9%", desc: "First-generation partners you personally introduce to AeroVolt.", width: 90 },
          { level: "Level 2", pct: "7%", desc: "Partners introduced by your Level 1 members.", width: 70 },
          { level: "Level 3", pct: "4%", desc: "Third-generation network growth.", width: 40 },
          { level: "Level 4", pct: "2%", desc: "Fourth-generation depth bonus.", width: 20 },
          { level: "Level 5", pct: "1%", desc: "Fifth-generation — deepest referral tier.", width: 10 },
        ].map((l, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="flex items-center gap-4 p-4 rounded-xl glass-card glass-card-hover transition-all duration-500">
            <div className="font-display text-2xl font-bold text-primary glow-text w-16 text-center">{l.pct}</div>
            <div className="flex-1">
              <div className="font-display text-sm font-semibold">{l.level}</div>
              <div className="text-xs text-muted-foreground">{l.desc}</div>
            </div>
            <div className="w-full max-w-[120px] h-2 rounded-full bg-muted overflow-hidden">
              <motion.div className="h-full energy-gradient rounded-full" initial={{ width: 0 }} whileInView={{ width: `${l.width}%` }} viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.8 }} />
            </div>
          </motion.div>
        ))}
      </div>
      <div className="text-center mt-8">
        <p className="text-sm text-muted-foreground max-w-xl mx-auto">
          Total referral pool: <span className="text-primary font-semibold">23%</span> across 5 levels. Commissions are calculated on each referred partner's initial package activation amount.
        </p>
      </div>
    </Section>

    {/* Benefits */}
    <Section className="bg-secondary/20">
      <SectionHeading eyebrow="Network Benefits" title="Why Build a Team?" />
      <div className="grid md:grid-cols-3 gap-6">
        {[
          { icon: TrendingUp, title: "Passive Growth", desc: "As your network expands, earn ongoing commissions from multiple levels without active selling. Your team's participation drives your earnings." },
          { icon: Award, title: "Rank Advancement", desc: "Team volume contributes directly to your rank progression. Higher ranks unlock exclusive bonuses, rewards, and recognition at AeroVolt events." },
          { icon: Network, title: "Compound Effect", desc: "Each new partner adds to your binary volume and referral depth, creating a compounding effect that grows exponentially over time." },
        ].map((b, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.15 }} className="p-6 rounded-2xl glass-card glass-card-hover group transition-all duration-500">
            <div className="w-14 h-14 rounded-xl energy-gradient flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_hsl(155_80%_50%/0.3)] transition-shadow">
              <b.icon className="w-7 h-7 text-primary-foreground" />
            </div>
            <h3 className="font-display text-base font-semibold mb-2">{b.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* CTA */}
    <Section>
      <div className="text-center max-w-2xl mx-auto">
        <Users className="w-12 h-12 text-primary mx-auto mb-6 animate-pulse-glow" />
        <h2 className="text-3xl md:text-4xl font-bold mb-4">Start Building Your Network Today</h2>
        <p className="text-muted-foreground mb-8">Join a growing community of partners earning through renewable energy participation and network growth.</p>
        <Link to="/packages" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all glow-box hover:glow-box-hover text-sm">
          Choose Your Plan <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </Section>
  </div>
);

export default AffiliateNetwork;
